import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  Badge,
  Card,
  CardBody,
  Input,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Textarea,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  useToast,
  useColorModeValue,
  Icon,
  Tooltip,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Alert,
  AlertIcon,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Checkbox,
} from '@chakra-ui/react';
import { 
  FiPlus, 
  FiEdit, 
  FiTrash2, 
  FiEye, 
  FiMoreVertical,
  FiPackage,
  FiTrendingUp,
  FiAlertTriangle,
  FiMinus,
  FiPlus as FiPlusIcon,
  FiRefreshCw
} from 'react-icons/fi';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { api } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { useCurrency } from '../contexts/CurrencyContext';

const Inventory = () => {
  const { t, currentLanguage } = useLanguage();
  const { formatCurrency } = useCurrency();
  const toast = useToast();
  const queryClient = useQueryClient();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter] = useState('');
  const [stockFilter, setStockFilter] = useState('');
  const [sectionFilter, setSectionFilter] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  
  const { isOpen: isNewOpen, onOpen: onNewOpen, onClose: onNewClose } = useDisclosure();
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isMovementOpen, onOpen: onMovementOpen, onClose: onMovementClose } = useDisclosure();
  const [selectedItem, setSelectedItem] = useState(null);
  const [editItem, setEditItem] = useState({
    id: '',
    name_en: '',
    name_de: '',
    item_class: '',
    item_subclass: '',
    uom_id: '',
    section_id: '',
    stock_quantity: '',
    unit_price: '',
    min_stock_level: '',
    width_mm: '',
    height_mm: '',
    depth_mm: '',
    radius_mm: '',
    weight_kg: '',
    color: '',
    material: '',
    supplier: '',
    supplier_sku: '',
    storage_building: '',
    storage_aisle: '',
    storage_space: '',
    location: '',
    location_note: '',
    is_external: false,
    description: ''
  });
  const [newItem, setNewItem] = useState({
    name_en: '',
    name_de: '',
    item_class: '',
    item_subclass: '',
    uom_id: '',
    section_id: '',
    stock_quantity: '',
    unit_price: '',
    min_stock_level: '',
    width_mm: '',
    height_mm: '',
    depth_mm: '',
    radius_mm: '',
    weight_kg: '',
    color: '',
    material: '',
    storage_building: '',
    storage_aisle: '',
    storage_space: '',
    location_note: '',
    location_note_checked: false,
    is_external: false,
    description: ''
  });
  const [newMovement, setNewMovement] = useState({
    type: 'in',
    quantity: '',
    notes: ''
  });

  // movements görünümü kaldırıldığı için tarih yerelleştirme yardımcılarına artık ihtiyaç yok

  const { data: inventory, isLoading } = useQuery('inventory', () =>
    api.get('/api/inventory').then(res => res.data.data.items)
  );
  const { data: metaUnits } = useQuery('metaUnits', () => api.get('/api/meta/units').then(res => res.data.data.units));
  const { data: metaSections } = useQuery('metaSections', () => api.get('/api/meta/sections').then(res => res.data.data.sections));
  // const { data: inventoryCategories } = useQuery('categories', () => api.get('/api/inventory/categories').then(res => res.data.data));
  const [newSection, setNewSection] = useState({ code: '', name_en: '', name_de: '', description: '' });
  const { isOpen: isNewSectionOpen, onOpen: onNewSectionOpen, onClose: onNewSectionClose } = useDisclosure();
  const createSectionMutation = useMutation(
    (payload) => api.post('/api/meta/sections', payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('metaSections');
        toast({ title: t('common.success'), status: 'success' });
        onNewSectionClose();
        setNewSection({ code: '', name_en: '', name_de: '', description: '' });
      },
      onError: () => {
        toast({ title: t('common.error'), status: 'error' });
      }
    }
  );

  const deleteSectionMutation = useMutation(
    (id) => api.delete(`/api/meta/sections/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('metaSections');
        toast({ title: t('common.success'), status: 'success' });
      },
      onError: () => {
        toast({ title: t('common.error'), status: 'error' });
      }
    }
  );

  const handleDeleteSection = (id) => {
    if (window.confirm(t('messages.confirm_delete'))) {
      deleteSectionMutation.mutate(id);
    }
  };

  const deleteUnitMutation = useMutation(
    (id) => api.delete(`/api/meta/units/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('metaUnits');
        toast({ title: t('common.success'), status: 'success' });
      },
      onError: () => {
        toast({ title: t('common.error'), status: 'error' });
      }
    }
  );

  const handleDeleteUnit = (id) => {
    if (window.confirm(t('messages.confirm_delete'))) {
      deleteUnitMutation.mutate(id);
    }
  };

  // movements sorgusu geçici olarak devre dışı (endpoint kullanım dışı)

  const createItemMutation = useMutation(
    (data) => api.post('/api/inventory', data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('inventory');
        toast({
          title: t('common.success'),
          description: t('inventory.created_success'),
          status: 'success',
        });
        onNewClose();
        setNewItem({
          name_en: '', name_de: '', item_class: '', item_subclass: '',
          uom_id: '', section_id: '', stock_quantity: '', unit_price: '', min_stock_level: '',
          width_mm: '', height_mm: '', depth_mm: '', radius_mm: '', weight_kg: '',
          storage_building: '', storage_aisle: '', storage_space: '',
          location_note: '', location_note_checked: false, description: ''
        });
      },
      onError: (error) => {
        toast({
          title: t('common.error'),
          description: error.response?.data?.message || t('messages.server_error'),
          status: 'error',
        });
      },
    }
  );

  const deleteItemMutation = useMutation(
    (id) => api.delete(`/api/inventory/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('inventory');
        toast({
          title: t('common.success'),
          description: t('inventory.deleted_success'),
          status: 'success',
        });
      },
      onError: (error) => {
        toast({
          title: t('common.error'),
          description: error.response?.data?.message || t('messages.server_error'),
          status: 'error',
        });
      },
    }
  );

  const createMovementMutation = useMutation(
    ({ id, payload }) => api.post(`/api/inventory/${id}/movement`, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('inventory');
        queryClient.invalidateQueries('movements');
        toast({
          title: t('common.success'),
          description: t('inventory.movement_created'),
          status: 'success',
        });
        onMovementClose();
        setNewMovement({ type: 'in', quantity: '', notes: '' });
      },
      onError: (error) => {
        toast({
          title: t('common.error'),
          description: error.response?.data?.message || t('messages.server_error'),
          status: 'error',
        });
      },
    }
  );

  const filteredItems = inventory?.filter(item => {
    const displayName = currentLanguage === 'de' ? (item.name_de || item.name_en || item.name) : (item.name_en || item.name_de || item.name);
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = (displayName || '').toLowerCase().includes(searchLower) ||
                         (item.description || '').toLowerCase().includes(searchLower) ||
                         (item.category || '').toLowerCase().includes(searchLower) ||
                         (item.supplier || '').toLowerCase().includes(searchLower) ||
                         (item.is_external && 'external'.includes(searchLower));
    const matchesCategory = !categoryFilter || item.category === categoryFilter;
    const matchesSection = !sectionFilter || item.section_id === sectionFilter;
    const matchesStock = !stockFilter || 
      (stockFilter === 'low' && Number(item.stock_quantity || 0) <= Number(item.min_stock_level || 0)) ||
      (stockFilter === 'out' && Number(item.stock_quantity || 0) === 0) ||
      (stockFilter === 'normal' && Number(item.stock_quantity || 0) > Number(item.min_stock_level || 0)) ||
      (stockFilter === 'external' && item.is_external);
    return matchesSearch && matchesCategory && matchesSection && matchesStock;
  }) || [];

  const sortedItems = [...filteredItems].sort((a, b) => {
    const aValue = sortBy === 'name' ? (currentLanguage === 'de' ? (a.name_de || a.name_en || a.name) : (a.name_en || a.name_de || a.name)) : a[sortBy];
    const bValue = sortBy === 'name' ? (currentLanguage === 'de' ? (b.name_de || b.name_en || b.name) : (b.name_en || b.name_de || b.name)) : b[sortBy];
    
    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  // Pagination logic
  const totalItems = sortedItems.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedItems = sortedItems.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, categoryFilter, stockFilter, sectionFilter, sortBy, sortOrder]);

  // const categories = [...new Set(inventory?.map(item => item.category).filter(Boolean))] || [];

  const getStockStatus = (item) => {
    if (item.is_external) return { status: 'external', color: 'orange', text: 'EXTERNAL' };
    const qty = Number(item.stock_quantity || 0);
    const minLvl = Number(item.min_stock_level || 0);
    if (qty === 0) return { status: 'out', color: 'red', text: t('inventory.status.out') };
    if (qty <= minLvl) return { status: 'low', color: 'orange', text: t('inventory.status.low') };
    return { status: 'normal', color: 'green', text: t('inventory.status.normal') };
  };

  // const getMovementTypeColor = (type) => {
  //   switch (type) {
  //     case 'in': return 'green';
  //     case 'out': return 'red';
  //     case 'adjustment': return 'blue';
  //     default: return 'gray';
  //   }
  // };

  const handleCreateItem = () => {
    const payload = {
      name: newItem.name,
      name_en: newItem.name_en || newItem.name,
      name_de: newItem.name_de || newItem.name,
      item_class: newItem.item_class || null,
      item_subclass: newItem.item_subclass || null,
      is_external: newItem.is_external || false,
      uom_id: newItem.uom_id ? Number(newItem.uom_id) : null,
      section_id: newItem.section_id ? Number(newItem.section_id) : null,
      stock_quantity: newItem.stock_quantity ? Number(newItem.stock_quantity) : 0,
      unit_price: newItem.unit_price ? Number(newItem.unit_price) : 0,
      min_stock_level: newItem.min_stock_level ? Number(newItem.min_stock_level) : 0,
      width_mm: newItem.width_mm ? Number(newItem.width_mm) : null,
      height_mm: newItem.height_mm ? Number(newItem.height_mm) : null,
      depth_mm: newItem.depth_mm ? Number(newItem.depth_mm) : null,
      radius_mm: newItem.radius_mm ? Number(newItem.radius_mm) : null,
      weight_kg: newItem.weight_kg ? Number(newItem.weight_kg) : null,
      storage_building: newItem.storage_building ? Number(newItem.storage_building) : null,
      storage_aisle: newItem.storage_aisle || null,
      storage_space: newItem.storage_space || null,
      location_note: newItem.location_note || null,
      location_note_checked: !!newItem.location_note_checked,
      description: newItem.description || null,
    };
    createItemMutation.mutate(payload);
  };

  const handleDeleteItem = (id) => {
    if (window.confirm(t('messages.confirm_delete'))) {
      deleteItemMutation.mutate(id);
    }
  };

  const handleViewItem = (item) => {
    setSelectedItem(item);
    onViewOpen();
  };

  const handleAddMovement = () => {
    if (selectedItem) {
      createMovementMutation.mutate({
        id: selectedItem.id,
        payload: {
          movement_type: newMovement.type,
          quantity: Number(newMovement.quantity || 0),
          reason: 'manual',
          reference: newMovement.notes || ''
        }
      });
    }
  };

  const handleOpenMovement = (item) => {
    setSelectedItem(item);
    onMovementOpen();
  };

  const stats = [
    {
      label: t('inventory.total_items'),
      value: inventory?.length || 0,
      icon: FiPackage,
      color: 'blue',
    },
    {
      label: t('inventory.low_stock'),
      value: inventory?.filter(item => Number(item.stock_quantity || 0) <= Number(item.min_stock_level || 0)).length || 0,
      icon: FiAlertTriangle,
      color: 'orange',
    },
    {
      label: t('inventory.out_of_stock'),
      value: inventory?.filter(item => Number(item.stock_quantity || 0) === 0).length || 0,
      icon: FiMinus,
      color: 'red',
    },
    {
      label: t('inventory.total_value'),
      value: `${formatCurrency((inventory?.reduce((sum, item) => sum + (Number(item.stock_quantity || 0) * Number(item.unit_price || 0)), 0) || 0))}`,
      icon: FiTrendingUp,
      color: 'green',
    },
  ];

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg">{t('inventory.title')}</Heading>
        <HStack>
          <Button leftIcon={<FiPlus />} colorScheme="brand" onClick={onNewOpen}>
            {t('inventory.new_item')}
          </Button>
        </HStack>
      </Flex>

      {/* Stats */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={6}>
        {stats.map((stat, index) => (
          <Card key={index} bg={bgColor} border="1px" borderColor={borderColor}>
            <CardBody>
              <Flex justify="space-between" align="center">
                <Box>
                  <Stat>
                    <StatLabel color="gray.500" fontSize="sm">{stat.label}</StatLabel>
                    <StatNumber color={`${stat.color}.500`} fontSize="xl" fontWeight="bold">
                      {stat.value}
                    </StatNumber>
                  </Stat>
                </Box>
                <Box
                  p={3}
                  borderRadius="full"
                  bg={`${stat.color}.100`}
                  color={`${stat.color}.600`}
                >
                  <Icon as={stat.icon} boxSize={5} />
                </Box>
              </Flex>
            </CardBody>
          </Card>
        ))}
      </SimpleGrid>

      {/* Filters */}
      <Card bg={bgColor} border="1px" borderColor={borderColor} mb={6}>
        <CardBody>
          <Flex gap={4} wrap="wrap" align="end">
            <Box maxW="400px" flex={1}>
              <Input
                placeholder={t('inventory.search_placeholder', {defaultValue: 'Search by name, description, category, supplier, external...'})}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                height="40px"
              />
            </Box>

            <Select
              maxW="200px"
              height="40px"
              value={stockFilter}
              onChange={(e) => setStockFilter(e.target.value)}
              placeholder={t('inventory.stock_status')}
            >
              <option value="low">{t('inventory.status.low')}</option>
              <option value="out">{t('inventory.status.out')}</option>
              <option value="normal">{t('inventory.status.normal')}</option>
              <option value="external">{t('inventory.status.external')}</option>
            </Select>

            <Select
              maxW="200px"
              height="40px"
              value={sectionFilter}
              onChange={(e) => setSectionFilter(e.target.value)}
              placeholder={t('inventory.section')}
            >
              <option value="">{t('inventory.all_sections', {defaultValue: 'All Sections'})}</option>
              {metaSections?.map(section => (
                <option key={section.id} value={section.id}>
                  {currentLanguage === 'de' ? (section.name_de || section.name_en) : (section.name_en || section.name_de)}
                </option>
              ))}
            </Select>

            <Select
              maxW="200px"
              height="40px"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="name">{t('inventory.name')}</option>
              <option value="code">{t('inventory.code')}</option>
              <option value="quantity">{t('inventory.quantity')}</option>
              <option value="price">{t('inventory.price')}</option>
            </Select>

            <Select
              maxW="150px"
              height="40px"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            >
              <option value="asc">{t('common.asc')}</option>
              <option value="desc">{t('common.desc')}</option>
            </Select>
          </Flex>
        </CardBody>
      </Card>

      {/* Tabs: Inventory, Sections, Units */}
      <Tabs colorScheme="brand" isFitted>
        <TabList>
          <Tab>{t('inventory.title')}</Tab>
          <Tab>{t('settings.sections')}</Tab>
          <Tab>{t('inventory.units', {defaultValue: 'Units'})}</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            {/* Inventory Table */}
            <Card bg={bgColor} border="1px" borderColor={borderColor}>
              <CardBody>
          {isLoading ? (
            <Text textAlign="center" py={8}>{t('common.loading')}</Text>
          ) : sortedItems.length > 0 ? (
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>{t('inventory.sku', {defaultValue: 'SKU'})}</Th>
                  <Th>{t('inventory.name')}</Th>
                  <Th>{t('inventory.section', {defaultValue: 'Section'})}</Th>
                  <Th>{t('inventory.unit')}</Th>
                  <Th>{t('inventory.quantity')}</Th>
                  <Th>{t('common.unit_price')}</Th>
                  <Th>{t('inventory.status')}</Th>
                  <Th>{t('common.actions')}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {paginatedItems.map((item) => {
                  const stockStatus = getStockStatus(item);
                  return (
                    <Tr key={item.id} bg={item.is_external ? 'orange.50' : undefined}>
                      <Td fontWeight="medium">{item.sku}</Td>
                      <Td>
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="medium">{currentLanguage === 'de' ? (item.name_de || item.name_en || item.name) : (item.name_en || item.name_de || item.name)}</Text>
                          <Text fontSize="sm" color="gray.500">
                            {item.description?.substring(0, 50)}...
                          </Text>
                        </VStack>
                      </Td>
                      <Td>
                        {(item.section_name_en || item.section_name_de) ? (
                          <VStack align="start" spacing={0}>
                            <Text fontSize="sm">{currentLanguage === 'de' ? item.section_name_de : item.section_name_en}</Text>
                          </VStack>
                        ) : (
                          <Text color="gray.400">-</Text>
                        )}
                      </Td>
                      <Td>
                        {(item.uom_abbr_en || item.uom_abbr_de) ? (
                          <VStack align="start" spacing={0}>
                            <Text fontSize="sm">{currentLanguage === 'de' ? item.uom_abbr_de : item.uom_abbr_en}</Text>
                            <Text fontSize="xs" color="gray.500">{currentLanguage === 'de' ? item.uom_name_de : item.uom_name_en}</Text>
                          </VStack>
                        ) : (
                          <Text color="gray.400">{item.unit || '-'}</Text>
                        )}
                      </Td>
                      <Td>
                        <Text fontWeight="medium">
                          {item.stock_quantity} {item.uom_abbr_en || item.uom_abbr_de || ''}
                        </Text>
                      </Td>
                      <Td>
                        <Text fontWeight="medium" color="green.600">{formatCurrency(Number(item.unit_price || 0))}</Text>
                      </Td>
                      <Td>
                        <Badge colorScheme={stockStatus.color}>
                          {stockStatus.text}
                        </Badge>
                      </Td>
                      <Td>
                        <HStack spacing={2}>
                          <Tooltip label={t('common.view')}>
                            <IconButton
                              size="sm"
                              icon={<FiEye />}
                              variant="ghost"
                              onClick={() => handleViewItem(item)}
                            />
                          </Tooltip>
                          <Tooltip label={t('inventory.add_movement')}>
                            <IconButton
                              size="sm"
                              icon={<FiPlusIcon />}
                              variant="ghost"
                              onClick={() => handleOpenMovement(item)}
                            />
                          </Tooltip>
                          <Tooltip label={t('common.edit')}>
                            <IconButton
                              size="sm"
                              icon={<FiEdit />}
                              variant="ghost"
                              onClick={() => {
                                setEditItem({
                                  id: item.id,
                                  name_en: item.name_en || '',
                                  name_de: item.name_de || '',
                                  item_class: item.item_class || '',
                                  item_subclass: item.item_subclass || '',
                                  uom_id: item.uom_id || '',
                                  section_id: item.section_id || '',
                                  stock_quantity: item.stock_quantity || '',
                                  unit_price: item.unit_price || '',
                                  min_stock_level: item.min_stock_level || '',
                                  width_mm: item.width_mm || '',
                                  height_mm: item.height_mm || '',
                                  depth_mm: item.depth_mm || '',
                                  radius_mm: item.radius_mm || '',
                                  weight_kg: item.weight_kg || '',
                                  color: item.color || '',
                                  material: item.material || '',
                                  supplier: item.supplier || '',
                                  supplier_sku: item.supplier_sku || '',
                                  storage_building: item.storage_building || '',
                                  storage_aisle: item.storage_aisle || '',
                                  storage_space: item.storage_space || '',
                                  location: item.location || '',
                                  location_note: item.location_note || '',
                                  is_external: item.is_external || false,
                                  description: item.description || ''
                                });
                                onEditOpen();
                              }}
                            />
                          </Tooltip>
                          <Menu>
                            <Tooltip label={t('common.actions')}>
                              <MenuButton
                                as={IconButton}
                                size="sm"
                                icon={<FiMoreVertical />}
                                variant="ghost"
                              />
                            </Tooltip>
                            <MenuList>
                              <MenuItem icon={<FiRefreshCw />}>
                                {t('inventory.refresh_stock')}
                              </MenuItem>
                              <MenuItem icon={<FiPackage />}>
                                {t('inventory.view_movements')}
                              </MenuItem>
                              <MenuDivider />
                              <MenuItem 
                                icon={<FiTrash2 />} 
                                color="red.500"
                                onClick={() => handleDeleteItem(item.id)}
                              >
                                {t('common.delete')}
                              </MenuItem>
                            </MenuList>
                          </Menu>
                        </HStack>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          ) : (
            <VStack py={8} spacing={4}>
              <Text color="gray.500" fontSize="lg">
                {t('messages.no_data')}
              </Text>
              <Button leftIcon={<FiPlus />} colorScheme="brand" onClick={onNewOpen}>
                {t('inventory.new_item')}
              </Button>
            </VStack>
          )}
          {/* Pagination */}
          {totalItems > 0 && (
            <Box mt={6}>
              {/* Results info */}
              <Text fontSize="sm" color="gray.600" textAlign="center" mb={3}>
                {`${totalItems} ${t('inventory.records_found', {defaultValue: 'records found'})}, ${t('inventory.showing', {defaultValue: 'showing'})} ${startIndex + 1}-${Math.min(endIndex, totalItems)}`}
              </Text>
              
              {/* Pagination controls and items per page in one row */}
              <HStack justify="space-between" align="center">
                {/* Pagination controls */}
                <HStack spacing={2}>
                  <Button 
                    size="sm" 
                    onClick={() => setCurrentPage(1)} 
                    isDisabled={currentPage === 1}
                    variant="outline"
                  >
                    {t('common.first', {defaultValue: 'First'})}
                  </Button>
                  <Button 
                    size="sm" 
                    onClick={() => setCurrentPage(currentPage - 1)} 
                    isDisabled={currentPage === 1}
                    variant="outline"
                  >
                    {t('common.prev', {defaultValue: 'Prev'})}
                  </Button>
                  
                  {/* Page numbers */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <Button
                        key={pageNum}
                        size="sm"
                        onClick={() => setCurrentPage(pageNum)}
                        colorScheme={currentPage === pageNum ? "brand" : "gray"}
                        variant={currentPage === pageNum ? "solid" : "outline"}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                  
                  <Button 
                    size="sm" 
                    onClick={() => setCurrentPage(currentPage + 1)} 
                    isDisabled={currentPage === totalPages}
                    variant="outline"
                  >
                    {t('common.next', {defaultValue: 'Next'})}
                  </Button>
                  <Button 
                    size="sm" 
                    onClick={() => setCurrentPage(totalPages)} 
                    isDisabled={currentPage === totalPages}
                    variant="outline"
                  >
                    {t('common.last', {defaultValue: 'Last'})}
                  </Button>
                </HStack>
                
                {/* Items per page selector */}
                <HStack spacing={2} align="center">
                  <Text fontSize="sm" whiteSpace="nowrap">{t('inventory.items_per_page', {defaultValue: 'Items per page'})}:</Text>
                  <Select
                    size="sm"
                    maxW="80px"
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </Select>
                </HStack>
              </HStack>
            </Box>
          )}
              </CardBody>
            </Card>
          </TabPanel>
          <TabPanel>
            {/* Sections Table */}
            <Card bg={bgColor} border="1px" borderColor={borderColor}>
              <CardBody>
                <Flex justify="space-between" align="center" mb={4}>
                  <Heading size="md">{t('inventory.sections')}</Heading>
                  <Button leftIcon={<FiPlus />} colorScheme="brand" onClick={onNewSectionOpen}>
                    {t('inventory.new_section')}
                  </Button>
                </Flex>
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th>{t('sections.code')}</Th>
                      <Th>{t('inventory.section_name_en')}</Th>
                      <Th>{t('inventory.section_name_de')}</Th>
                      <Th>{t('common.actions')}</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {metaSections?.map((s) => (
                      <Tr key={s.id}>
                        <Td>{s.code}</Td>
                        <Td>{s.name_en}</Td>
                        <Td>{s.name_de}</Td>
                        <Td>
                          <HStack>
                            <IconButton size="sm" aria-label={t('common.delete')} icon={<FiTrash2 />} onClick={() => handleDeleteSection(s.id)} />
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </CardBody>
            </Card>
          </TabPanel>
          <TabPanel>
            {/* Units Table */}
            <Card bg={bgColor} border="1px" borderColor={borderColor}>
              <CardBody>
                <Flex justify="space-between" align="center" mb={4}>
                  <Heading size="md">{t('inventory.units')}</Heading>
                </Flex>
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th>{t('common.key')}</Th>
                      <Th>{t('inventory.unit_name_en', { defaultValue: 'Unit Name (EN)' })}</Th>
                      <Th>{t('inventory.unit_name_de', { defaultValue: 'Einheitsname (DE)' })}</Th>
                      <Th>{t('inventory.abbr_en', { defaultValue: 'Abbreviation (EN)' })}</Th>
                      <Th>{t('inventory.abbr_de', { defaultValue: 'Abkürzung (DE)' })}</Th>
                      <Th>{t('common.actions')}</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {metaUnits?.map((u) => (
                      <Tr key={u.id}>
                        <Td>{u.key}</Td>
                        <Td>{u.name_en}</Td>
                        <Td>{u.name_de}</Td>
                        <Td>{u.abbreviation_en}</Td>
                        <Td>{u.abbreviation_de}</Td>
                        <Td>
                          <HStack>
                            <IconButton size="sm" aria-label={t('common.delete')} icon={<FiTrash2 />} onClick={() => handleDeleteUnit(u.id)} />
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </CardBody>
            </Card>
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* New Section Modal */}
      <Modal isOpen={isNewSectionOpen} onClose={onNewSectionClose} size="sm">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('inventory.new_section')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>{t('sections.code')}</FormLabel>
                <Input value={newSection.code} onChange={(e)=>setNewSection({...newSection, code: e.target.value})} />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>{t('inventory.section_name_en')}</FormLabel>
                <Input value={newSection.name_en} onChange={(e)=>setNewSection({...newSection, name_en: e.target.value})} />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>{t('inventory.section_name_de')}</FormLabel>
                <Input value={newSection.name_de} onChange={(e)=>setNewSection({...newSection, name_de: e.target.value})} />
              </FormControl>
              <FormControl>
                <FormLabel>{t('inventory.description')}</FormLabel>
                <Textarea value={newSection.description} onChange={(e)=>setNewSection({...newSection, description: e.target.value})} rows={3} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onNewSectionClose}>{t('common.cancel')}</Button>
            <Button colorScheme="brand" onClick={()=>createSectionMutation.mutate(newSection)} isLoading={createSectionMutation.isLoading}>
              {t('common.save')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* New Item Modal */}
      <Modal isOpen={isNewOpen} onClose={onNewClose} size="4xl">
        <ModalOverlay />
        <ModalContent maxH="90vh" overflowY="auto">
          <ModalHeader bg="brand.500" color="white" borderRadius="md md 0 0">
            <HStack>
              <Icon as={FiPlus} />
              <Text>{t('inventory.new_item')}</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody p={6}>
            <VStack spacing={6}>
              {/* Basic Information */}
              <Box w="full" p={4} bg="gray.50" borderRadius="md">
                <Text fontSize="md" fontWeight="bold" mb={4} color="gray.700">
                  {t('inventory.basic_information')}
                </Text>
                <VStack spacing={4}>
                  <HStack spacing={4} w="full">
                    <FormControl>
                      <FormLabel>{t('inventory.name_de')}</FormLabel>
                      <Input value={newItem.name_de} onChange={(e)=>setNewItem({...newItem, name_de: e.target.value})} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>{t('inventory.name_en')}</FormLabel>
                      <Input value={newItem.name_en} onChange={(e)=>setNewItem({...newItem, name_en: e.target.value})} />
                    </FormControl>
                  </HStack>
                  
                  <HStack spacing={4} w="full">
                    <FormControl>
                      <FormLabel>{t('inventory.class')}</FormLabel>
                      <Input value={newItem.item_class} onChange={(e)=>setNewItem({...newItem, item_class: e.target.value})} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>{t('inventory.subclass')}</FormLabel>
                      <Input value={newItem.item_subclass} onChange={(e)=>setNewItem({...newItem, item_subclass: e.target.value})} />
                    </FormControl>
                  </HStack>
                  
                  <HStack spacing={4} w="full">
                    <FormControl>
                      <FormLabel>{t('inventory.unit')}</FormLabel>
                      <Select value={newItem.uom_id} onChange={(e)=>setNewItem({...newItem, uom_id: e.target.value})}>
                        <option value="">{t('common.select')}</option>
                        {metaUnits?.map(unit => (
                          <option key={unit.id} value={unit.id}>
                            {currentLanguage === 'de' ? unit.name_de : unit.name_en}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormLabel>{t('inventory.section')}</FormLabel>
                      <Select value={newItem.section_id} onChange={(e)=>setNewItem({...newItem, section_id: e.target.value})}>
                        <option value="">{t('common.select')}</option>
                        {metaSections?.map(section => (
                          <option key={section.id} value={section.id}>
                            {currentLanguage === 'de' ? section.name_de : section.name_en}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  </HStack>
                  
                  <HStack spacing={4} w="full">
                    <FormControl>
                      <FormLabel>{t('inventory.external')}</FormLabel>
                      <Checkbox 
                        isChecked={newItem.is_external} 
                        onChange={(e)=>setNewItem({...newItem, is_external: e.target.checked})}
                        colorScheme="orange"
                      >
                        {t('inventory.external_item')}
                      </Checkbox>
                    </FormControl>
                  </HStack>
                </VStack>
              </Box>

              {/* Stock & Pricing */}
              <Box w="full" p={4} bg="blue.50" borderRadius="md">
                <Text fontSize="md" fontWeight="bold" mb={4} color="blue.700">
                  {t('inventory.stock_pricing')}
                </Text>
                <HStack spacing={4} w="full">
                  <FormControl>
                    <FormLabel>{t('inventory.stock_quantity')}</FormLabel>
                    <Input type="number" value={newItem.stock_quantity} onChange={(e)=>setNewItem({...newItem, stock_quantity: e.target.value})} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>{t('inventory.unit_price')}</FormLabel>
                    <Input type="number" step="0.01" value={newItem.unit_price} onChange={(e)=>setNewItem({...newItem, unit_price: e.target.value})} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>{t('inventory.min_stock_level')}</FormLabel>
                    <Input type="number" value={newItem.min_stock_level} onChange={(e)=>setNewItem({...newItem, min_stock_level: e.target.value})} />
                  </FormControl>
                </HStack>
              </Box>
              
              {/* Physical Properties */}
              <Box w="full" p={4} bg="green.50" borderRadius="md">
                <Text fontSize="md" fontWeight="bold" mb={4} color="green.700">
                  {t('inventory.physical_properties')}
                </Text>
                <VStack spacing={4}>
                  <HStack spacing={4} w="full">
                    <FormControl>
                      <FormLabel>{t('inventory.width_mm')}</FormLabel>
                      <Input type="number" value={newItem.width_mm} onChange={(e)=>setNewItem({...newItem, width_mm: e.target.value})} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>{t('inventory.height_mm')}</FormLabel>
                      <Input type="number" value={newItem.height_mm} onChange={(e)=>setNewItem({...newItem, height_mm: e.target.value})} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>{t('inventory.depth_mm')}</FormLabel>
                      <Input type="number" value={newItem.depth_mm} onChange={(e)=>setNewItem({...newItem, depth_mm: e.target.value})} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>{t('inventory.radius_mm')}</FormLabel>
                      <Input type="number" value={newItem.radius_mm} onChange={(e)=>setNewItem({...newItem, radius_mm: e.target.value})} />
                    </FormControl>
                  </HStack>
                  
                  <HStack spacing={4} w="full">
                    <FormControl>
                      <FormLabel>{t('inventory.weight_kg')}</FormLabel>
                      <Input type="number" step="0.01" value={newItem.weight_kg} onChange={(e)=>setNewItem({...newItem, weight_kg: e.target.value})} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>{t('inventory.color')}</FormLabel>
                      <Input value={newItem.color} onChange={(e)=>setNewItem({...newItem, color: e.target.value})} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>{t('inventory.material')}</FormLabel>
                      <Input value={newItem.material} onChange={(e)=>setNewItem({...newItem, material: e.target.value})} />
                    </FormControl>
                  </HStack>
                </VStack>
              </Box>

              {/* Storage & Location */}
              <Box w="full" p={4} bg="purple.50" borderRadius="md">
                <Text fontSize="md" fontWeight="bold" mb={4} color="purple.700">
                  {t('inventory.storage_location')}
                </Text>
                <VStack spacing={4}>
                  <HStack spacing={4} w="full">
                    <FormControl>
                      <FormLabel>{t('inventory.storage_building')}</FormLabel>
                      <Input value={newItem.storage_building} onChange={(e)=>setNewItem({...newItem, storage_building: e.target.value})} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>{t('inventory.aisle')}</FormLabel>
                      <Input value={newItem.storage_aisle} onChange={(e)=>setNewItem({...newItem, storage_aisle: e.target.value})} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>{t('inventory.space')}</FormLabel>
                      <Input value={newItem.storage_space} onChange={(e)=>setNewItem({...newItem, storage_space: e.target.value})} />
                    </FormControl>
                  </HStack>
                  
                  <FormControl>
                    <FormLabel>{t('inventory.location_note')}</FormLabel>
                    <Textarea value={newItem.location_note} onChange={(e)=>setNewItem({...newItem, location_note: e.target.value})} rows={2} />
                  </FormControl>
                </VStack>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter bg="gray.50" borderRadius="0 0 md md">
            <HStack spacing={3}>
              <Button variant="ghost" onClick={onNewClose} size="lg">
                {t('common.cancel')}
              </Button>
              <Button 
                colorScheme="brand" 
                size="lg"
                leftIcon={<Icon as={FiPlus} />}
                onClick={handleCreateItem}
                isLoading={createItemMutation.isLoading}
              >
                {t('common.save')}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* View Item Modal */}
      <Modal isOpen={isViewOpen} onClose={onViewClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('inventory.view_item')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedItem && (
              <Tabs>
                <TabList>
                  <Tab>{t('common.details')}</Tab>
                  <Tab>{t('inventory.sub_descriptions')}</Tab>
                </TabList>
                <TabPanels>
                  <TabPanel>
                    <VStack spacing={4} align="stretch">
                      <HStack justify="space-between">
                        <Text fontWeight="bold">{t('inventory.sku', {defaultValue: 'SKU'})}:</Text>
                        <Text>{selectedItem.sku || '-'}</Text>
                      </HStack>
                      
                      <HStack justify="space-between">
                        <Text fontWeight="bold">{t('inventory.name')}:</Text>
                        <Text>{currentLanguage === 'de' ? (selectedItem.name_de || selectedItem.name_en || selectedItem.name) : (selectedItem.name_en || selectedItem.name_de || selectedItem.name)}</Text>
                      </HStack>
                      
                      <HStack justify="space-between">
                        <Text fontWeight="bold">{t('inventory.category')}:</Text>
                        <Badge variant="outline">{selectedItem.category || '-'}</Badge>
                      </HStack>
                      
                      <HStack justify="space-between">
                        <Text fontWeight="bold">{t('inventory.quantity')}:</Text>
                        <Text>{selectedItem.stock_quantity || 0} {selectedItem.uom_abbr_en || selectedItem.uom_abbr_de || selectedItem.unit || ''}</Text>
                      </HStack>
                      
                      <HStack justify="space-between">
                        <Text fontWeight="bold">{t('inventory.unit_price')}:</Text>
                        <Text color="green.600" fontWeight="bold">
                          {formatCurrency(Number(selectedItem.unit_price || 0))}
                        </Text>
                      </HStack>
                      
                      <HStack justify="space-between">
                        <Text fontWeight="bold">{t('inventory.total_value', {defaultValue: 'Total Value'})}:</Text>
                        <Text>{formatCurrency(Number(selectedItem.unit_price || 0) * Number(selectedItem.stock_quantity || 0))}</Text>
                      </HStack>
                      
                      <HStack justify="space-between">
                        <Text fontWeight="bold">{t('inventory.supplier')}:</Text>
                        <Text>{selectedItem.supplier || selectedItem.supplier_info || '-'}</Text>
                      </HStack>
                      
                      <HStack justify="space-between">
                        <Text fontWeight="bold">{t('inventory.location')}:</Text>
                        <Text>{selectedItem.location || selectedItem.location_note || '-'}</Text>
                      </HStack>
                      
                      {selectedItem.description && (
                        <Box>
                          <Text fontWeight="bold" mb={2}>{t('inventory.description')}:</Text>
                          <Text>{selectedItem.description}</Text>
                        </Box>
                      )}
                    </VStack>
                  </TabPanel>
                  <TabPanel>
                    <SubDescriptionsManager itemType="product" itemId={selectedItem.id} />
                  </TabPanel>
                </TabPanels>
              </Tabs>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onViewClose}>
              {t('common.close')}
            </Button>
            <Button colorScheme="brand" onClick={() => {
              setEditItem({
                id: selectedItem.id,
                name_en: selectedItem.name_en || '',
                name_de: selectedItem.name_de || '',
                category: selectedItem.category || '',
                item_class: selectedItem.item_class || '',
                item_subclass: selectedItem.item_subclass || '',
                uom_id: selectedItem.uom_id || '',
                section_id: selectedItem.section_id || '',
                stock_quantity: selectedItem.stock_quantity || '',
                unit_price: selectedItem.unit_price || '',
                min_stock_level: selectedItem.min_stock_level || '',
                width_mm: selectedItem.width_mm || '',
                height_mm: selectedItem.height_mm || '',
                depth_mm: selectedItem.depth_mm || '',
                radius_mm: selectedItem.radius_mm || '',
                weight_kg: selectedItem.weight_kg || '',
                color: selectedItem.color || '',
                material: selectedItem.material || '',
                supplier: selectedItem.supplier || '',
                supplier_sku: selectedItem.supplier_sku || '',
                storage_building: selectedItem.storage_building || '',
                storage_aisle: selectedItem.storage_aisle || '',
                storage_space: selectedItem.storage_space || '',
                location: selectedItem.location || '',
                location_note: selectedItem.location_note || '',
                is_external: selectedItem.is_external || false,
                description: selectedItem.description || ''
              });
              onViewClose();
              onEditOpen();
            }}>
              {t('common.edit')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Add Movement Modal */}
      <Modal isOpen={isMovementOpen} onClose={onMovementClose} size="sm">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('inventory.add_movement')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedItem && (
              <VStack spacing={4}>
                <Alert status="info">
                  <AlertIcon />
                  {t('inventory.movement_for')}: {selectedItem.name}
                </Alert>
                
                <FormControl isRequired>
                  <FormLabel>{t('inventory.movement_type')}</FormLabel>
                  <Select
                    value={newMovement.type}
                    onChange={(e) => setNewMovement({...newMovement, type: e.target.value})}
                  >
                    <option value="in">{t('inventory.movement_type.in')}</option>
                    <option value="out">{t('inventory.movement_type.out')}</option>
                    <option value="adjustment">{t('inventory.movement_type.adjustment')}</option>
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>{t('inventory.movement_quantity')}</FormLabel>
                  <NumberInput
                    value={newMovement.quantity}
                    onChange={(value) => setNewMovement({...newMovement, quantity: value})}
                    min={0}
                  >
                    <NumberInputField placeholder="0" />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>

                <FormControl>
                  <FormLabel>{t('inventory.movement_notes')}</FormLabel>
                  <Textarea
                    value={newMovement.notes}
                    onChange={(e) => setNewMovement({...newMovement, notes: e.target.value})}
                    placeholder={t('inventory.movement_notes')}
                    rows={3}
                  />
                </FormControl>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onMovementClose}>
              {t('common.cancel')}
            </Button>
            <Button 
              colorScheme="brand" 
              onClick={handleAddMovement}
              isLoading={createMovementMutation.isLoading}
            >
              {t('common.save')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit Item Modal */}
      <Modal isOpen={isEditOpen} onClose={onEditClose} size="4xl">
        <ModalOverlay />
        <ModalContent maxH="90vh" overflowY="auto">
          <ModalHeader bg="brand.500" color="white" borderRadius="md md 0 0">
            <HStack>
              <Icon as={FiEdit} />
              <Text>{t('inventory.edit_item')}</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody p={6}>
            <VStack spacing={6}>
              {/* Basic Information */}
              <Box w="full" p={4} bg="gray.50" borderRadius="md">
                                  <Text fontSize="md" fontWeight="bold" mb={4} color="gray.700">
                   {t('inventory.basic_information')}
                  </Text>
                <VStack spacing={4}>
                  <HStack spacing={4} w="full">
                    <FormControl>
                      <FormLabel>{t('inventory.name_de')}</FormLabel>
                      <Input value={editItem.name_de} onChange={(e)=>setEditItem({...editItem, name_de: e.target.value})} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>{t('inventory.name_en')}</FormLabel>
                      <Input value={editItem.name_en} onChange={(e)=>setEditItem({...editItem, name_en: e.target.value})} />
                    </FormControl>
                  </HStack>
                  
                  <HStack spacing={4} w="full">
                    <FormControl>
                      <FormLabel>{t('inventory.class')}</FormLabel>
                      <Input value={editItem.item_class} onChange={(e)=>setEditItem({...editItem, item_class: e.target.value})} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>{t('inventory.subclass')}</FormLabel>
                      <Input value={editItem.item_subclass} onChange={(e)=>setEditItem({...editItem, item_subclass: e.target.value})} />
                    </FormControl>
                  </HStack>
                  
                  <HStack spacing={4} w="full">
                    <FormControl>
                      <FormLabel>{t('inventory.unit')}</FormLabel>
                      <Select value={editItem.uom_id} onChange={(e)=>setEditItem({...editItem, uom_id: e.target.value})}>
                        <option value="">{t('common.select')}</option>
                        {metaUnits?.map(unit => (
                          <option key={unit.id} value={unit.id}>
                            {currentLanguage === 'de' ? unit.name_de : unit.name_en}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormLabel>{t('inventory.section')}</FormLabel>
                      <Select value={editItem.section_id} onChange={(e)=>setEditItem({...editItem, section_id: e.target.value})}>
                        <option value="">{t('common.select')}</option>
                        {metaSections?.map(section => (
                          <option key={section.id} value={section.id}>
                            {currentLanguage === 'de' ? section.name_de : section.name_en}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  </HStack>
                  
                  <HStack spacing={4} w="full">
                    <FormControl>
                      <FormLabel>{t('inventory.external')}</FormLabel>
                      <Checkbox 
                        isChecked={editItem.is_external} 
                        onChange={(e)=>setEditItem({...editItem, is_external: e.target.checked})}
                        colorScheme="orange"
                      >
                        {t('inventory.external_item')}
                      </Checkbox>
                    </FormControl>
                  </HStack>
                </VStack>
              </Box>

              {/* Stock & Pricing */}
              <Box w="full" p={4} bg="blue.50" borderRadius="md">
                                <Text fontSize="md" fontWeight="bold" mb={4} color="blue.700">
                 {t('inventory.stock_pricing')}
                </Text>
                <HStack spacing={4} w="full">
                  <FormControl>
                    <FormLabel>{t('inventory.stock_quantity')}</FormLabel>
                    <Input type="number" value={editItem.stock_quantity} onChange={(e)=>setEditItem({...editItem, stock_quantity: e.target.value})} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>{t('inventory.unit_price')}</FormLabel>
                    <Input type="number" step="0.01" value={editItem.unit_price} onChange={(e)=>setEditItem({...editItem, unit_price: e.target.value})} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>{t('inventory.min_stock_level')}</FormLabel>
                    <Input type="number" value={editItem.min_stock_level} onChange={(e)=>setEditItem({...editItem, min_stock_level: e.target.value})} />
                  </FormControl>
                </HStack>
              </Box>
              
              {/* Physical Properties */}
              <Box w="full" p={4} bg="green.50" borderRadius="md">
                                <Text fontSize="md" fontWeight="bold" mb={4} color="green.700">
                   {t('inventory.physical_properties')}
                </Text>
                <VStack spacing={4}>
                  <HStack spacing={4} w="full">
                    <FormControl>
                      <FormLabel>{t('inventory.width_mm')}</FormLabel>
                      <Input type="number" value={editItem.width_mm} onChange={(e)=>setEditItem({...editItem, width_mm: e.target.value})} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>{t('inventory.height_mm')}</FormLabel>
                      <Input type="number" value={editItem.height_mm} onChange={(e)=>setEditItem({...editItem, height_mm: e.target.value})} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>{t('inventory.depth_mm')}</FormLabel>
                      <Input type="number" value={editItem.depth_mm} onChange={(e)=>setEditItem({...editItem, depth_mm: e.target.value})} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>{t('inventory.radius_mm')}</FormLabel>
                      <Input type="number" value={editItem.radius_mm} onChange={(e)=>setEditItem({...editItem, radius_mm: e.target.value})} />
                    </FormControl>
                  </HStack>
                  
                  <HStack spacing={4} w="full">
                    <FormControl>
                      <FormLabel>{t('inventory.weight_kg')}</FormLabel>
                      <Input type="number" step="0.01" value={editItem.weight_kg} onChange={(e)=>setEditItem({...editItem, weight_kg: e.target.value})} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>{t('inventory.color')}</FormLabel>
                      <Input value={editItem.color} onChange={(e)=>setEditItem({...editItem, color: e.target.value})} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>{t('inventory.material')}</FormLabel>
                      <Input value={editItem.material} onChange={(e)=>setEditItem({...editItem, material: e.target.value})} />
                    </FormControl>
                  </HStack>
                </VStack>
              </Box>

              {/* Storage & Location */}
              <Box w="full" p={4} bg="purple.50" borderRadius="md">
                                <Text fontSize="md" fontWeight="bold" mb={4} color="purple.700">
                   {t('inventory.storage_location')}
                </Text>
                <VStack spacing={4}>
                  <HStack spacing={4} w="full">
                    <FormControl>
                      <FormLabel>{t('inventory.storage_building')}</FormLabel>
                      <Input value={editItem.storage_building} onChange={(e)=>setEditItem({...editItem, storage_building: e.target.value})} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>{t('inventory.aisle')}</FormLabel>
                      <Input value={editItem.storage_aisle} onChange={(e)=>setEditItem({...editItem, storage_aisle: e.target.value})} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>{t('inventory.space')}</FormLabel>
                      <Input value={editItem.storage_space} onChange={(e)=>setEditItem({...editItem, storage_space: e.target.value})} />
                    </FormControl>
                  </HStack>
                  
                  <HStack spacing={4} w="full">
                    <FormControl>
                      <FormLabel>{t('inventory.location')}</FormLabel>
                      <Input value={editItem.location} onChange={(e)=>setEditItem({...editItem, location: e.target.value})} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>{t('inventory.location_note')}</FormLabel>
                      <Textarea value={editItem.location_note} onChange={(e)=>setEditItem({...editItem, location_note: e.target.value})} rows={2} />
                    </FormControl>
                  </HStack>
                </VStack>
              </Box>

              {/* Supplier Information */}
              <Box w="full" p={4} bg="orange.50" borderRadius="md">
                                <Text fontSize="md" fontWeight="bold" mb={4} color="orange.700">
                   {t('inventory.supplier_information')}
                </Text>
                <HStack spacing={4} w="full">
                  <FormControl>
                    <FormLabel>{t('inventory.supplier')}</FormLabel>
                    <Input value={editItem.supplier} onChange={(e)=>setEditItem({...editItem, supplier: e.target.value})} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>{t('inventory.supplier_sku')}</FormLabel>
                    <Input value={editItem.supplier_sku} onChange={(e)=>setEditItem({...editItem, supplier_sku: e.target.value})} />
                  </FormControl>
                </HStack>
              </Box>

              {/* Description */}
              <Box w="full" p={4} bg="yellow.50" borderRadius="md">
                                <Text fontSize="md" fontWeight="bold" mb={4} color="yellow.700">
                   {t('inventory.description')}
                </Text>
                <FormControl>
                  <Textarea 
                    value={editItem.description} 
                    onChange={(e)=>setEditItem({...editItem, description: e.target.value})} 
                    rows={4}
                    placeholder={t('inventory.enter_description')}
                  />
                </FormControl>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter bg="gray.50" borderRadius="0 0 md md">
            <HStack spacing={3}>
              <Button variant="ghost" onClick={onEditClose} size="lg">
                {t('common.cancel')}
              </Button>
              <Button 
                colorScheme="brand" 
                size="lg"
                leftIcon={<Icon as={FiEdit} />}
                onClick={async () => {
                  try {
                    await api.put(`/api/inventory/${editItem.id}`, editItem);
                    queryClient.invalidateQueries('inventory');
                    toast({ 
                      title: t('common.success'), 
                      description: t('inventory.item_updated_successfully'),
                      status: 'success' 
                    });
                    onEditClose();
                  } catch (e) {
                    toast({ 
                      title: t('common.error'), 
                      description: t('inventory.failed_to_update_item'),
                      status: 'error' 
                    });
                  }
                }}
              >
                {t('common.save')}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Inventory;

// SubDescriptionsManager component
const SubDescriptionsManager = ({ itemType, itemId }) => {
  const { t } = useLanguage();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState('');
  const toast = useToast();

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/api/meta/sub-descriptions', { params: { item_type: itemType, item_id: itemId } });
      setList(data?.data?.items || []);
    } catch (_) {
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (itemId) load(); }, [itemId]);

  const addOne = async () => {
    try {
      if (!input.trim()) return;
      await api.post('/api/meta/sub-descriptions', { item_type: itemType, item_id: itemId, sub_description: input.trim() });
      setInput('');
      load();
      toast({ title: t('common.success'), status: 'success' });
    } catch (e) {
      toast({ title: t('common.error'), status: 'error' });
    }
  };

  const removeOne = async (id) => {
    try {
      await api.delete(`/api/meta/sub-descriptions/${id}`);
      load();
    } catch (e) {}
  };

  return (
    <VStack align="stretch" spacing={3}>
      <HStack>
        <Input placeholder={t('common.add') || 'Add sub description'} value={input} onChange={(e)=> setInput(e.target.value)} />
        <Button leftIcon={<FiPlus />} colorScheme="blue" onClick={addOne} isDisabled={!input.trim()}>{t('common.add')}</Button>
      </HStack>
      <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" p={2}>
        {loading ? (
          <Text fontSize="sm" color="gray.500">{t('common.loading') || 'Loading...'}</Text>
        ) : (
          <VStack align="stretch" spacing={1}>
            {(list || []).map(row => (
              <HStack key={row.id} justify="space-between">
                <Text fontSize="sm">+ {row.sub_description}</Text>
                <IconButton aria-label="delete" size="sm" variant="ghost" colorScheme="red" icon={<FiTrash2 />} onClick={()=> removeOne(row.id)} />
              </HStack>
            ))}
          </VStack>
        )}
      </Box>
    </VStack>
  );
};

